import {
  AfterViewInit,
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  LOCALE_ID,
  output,
  signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Popover } from 'primeng/popover';
import { Button } from 'primeng/button';
import {
  CalendarOptions,
  DatesSetArg,
  EventApi,
  EventClickArg,
  EventContentArg,
  EventHoveringArg,
  EventInput,
} from '@fullcalendar/core';
import allLocales from '@fullcalendar/core/locales-all';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { scrollTimeForNearestEvent } from '@shared/utils/nearest-event-scroll.utils';
import { eventStatusColors } from '@shared/utils/event-color.utils';
import { EnEventStatus, EnEventType } from '@shared/dto';
import { DurationPipe } from '@shared/pipes/duration.pipe';
import { EventStatusTagComponent } from '../enum-tag/event-status-tag.component';

const EVENT_PREVIEW_SHOW_DELAY_MS = 200;
const EVENT_PREVIEW_HIDE_DELAY_MS = 150;
/** Above FullCalendar's `.fc-popover` (`z-index: 9999`). */
const EVENT_PREVIEW_BASE_Z_INDEX = 9000;
const EVENT_PREVIEW_GAP_PX = 8;
const EVENT_PREVIEW_VIEWPORT_INSET_PX = 8;
const FC_MORE_POPOVER_SELECTOR = '.fc-more-popover';

interface CalendarEventPreview {
  title: string;
  dotColor: string;
  patientName: string | null;
  start: Date | null;
  end: Date | null;
  allDay: boolean;
  durationSeconds: number | null;
  status: EnEventStatus | null;
  type: EnEventType | null;
}

/**
 * Visible calendar range as `YYYY-MM-DD` calendar days — the format the
 * `/session-events` API expects for its `start`/`end` filter (a full ISO datetime
 * with offset is rejected by the backend).
 */
export interface CalendarRange {
  start: string;
  end: string;
}

/**
 * Reduces a FullCalendar ISO boundary (e.g. `2026-06-29T00:00:00+02:00`) to the
 * `YYYY-MM-DD` day it represents. Takes the leading date component verbatim so the
 * calendar's displayed day is preserved without any timezone reinterpretation.
 */
export function calendarBoundaryToApiDay(isoBoundary: string): string {
  return isoBoundary.slice(0, 10);
}

/**
 * Same reduction for a boundary read imperatively off the FullCalendar API
 * (`view.activeStart` / `activeEnd`), which hands back a `Date` rather than a string.
 * Uses the local date parts so the day matches what the calendar displays.
 */
export function calendarDateToApiDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Disc + time + title chip. List view already has its own disc column, so it
 * keeps FullCalendar's default inner content.
 */
function calendarEventContent(arg: EventContentArg): { domNodes: Node[] } | undefined {
  if (arg.view.type.startsWith('list')) return undefined;

  const root = document.createElement('div');
  root.className = 'fc-event-card';

  const dot = document.createElement('span');
  dot.className = 'fc-event-card__dot';
  const dotColor = arg.borderColor || arg.backgroundColor;
  if (dotColor) {
    root.style.setProperty('--fc-event-card-dot', dotColor);
  }
  root.append(dot);

  if (arg.timeText) {
    const time = document.createElement('span');
    time.className = 'fc-event-card__time';
    time.textContent = arg.timeText;
    root.append(time);
  }

  const title = document.createElement('span');
  title.className = 'fc-event-card__title';
  title.textContent = arg.event.title ?? '';
  title.title = arg.event.title ?? '';
  root.append(title);

  return { domNodes: [root] };
}

/**
 * Docks `overlay` to the right of `anchor` (left if it would overflow), top-aligned.
 * Used so the session preview sits beside FullCalendar's day "more" list instead of
 * covering it from below.
 */
function positionOverlayBeside(overlay: HTMLElement, anchor: HTMLElement): void {
  const overlayRect = overlay.getBoundingClientRect();
  const anchorRect = anchor.getBoundingClientRect();
  const width = overlayRect.width;
  const height = overlayRect.height;
  if (!width || !height) return;

  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;
  const inset = EVENT_PREVIEW_VIEWPORT_INSET_PX;
  const gap = EVENT_PREVIEW_GAP_PX;

  let left = anchorRect.right + gap;
  if (left + width > viewportWidth - inset) {
    left = anchorRect.left - width - gap;
  }
  left = Math.min(Math.max(left, inset), Math.max(inset, viewportWidth - width - inset));

  let top = anchorRect.top;
  if (top + height > viewportHeight - inset) {
    top = viewportHeight - height - inset;
  }
  top = Math.max(inset, top);

  overlay.style.top = `${top + window.scrollY}px`;
  overlay.style.left = `${left + window.scrollX}px`;
}

/**
 * Shared FullCalendar wrapper. It owns the calendar's *look* — the theming that maps
 * FullCalendar's CSS variables onto the app's PrimeNG tokens (light/dark), the base
 * configuration (slot window, header, sticky dates) and the loading overlay — so every
 * calendar surface reads the same.
 *
 * Callers layer behaviour on top via `[options]` (shallow-merged OVER the base, so the
 * caller wins) and receive the imperative FullCalendar handle through `(ready)` /
 * `api` for drag-drop, resize, external drops, etc. Read-only surfaces (dashboard) pass
 * little or nothing; the interactive one (assignment) passes its full option set.
 *
 * On time-grid views it also scrolls to the event nearest to "now" once per visible
 * range (and again when {@link requestScrollToNearestEvent} is called) so day/week
 * agendas don't sit at the default morning `scrollTime` when the action is later.
 */
@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    FullCalendarModule,
    ProgressSpinnerModule,
    Popover,
    Button,
    DatePipe,
    DurationPipe,
    EventStatusTagComponent,
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
})
export class CalendarComponent implements AfterViewInit {
  private readonly locale = inject(LOCALE_ID);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventPopover = viewChild<Popover>('eventPopover');

  readonly preview = signal<CalendarEventPreview | null>(null);
  readonly previewBaseZIndex = EVENT_PREVIEW_BASE_Z_INDEX;

  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private previewTarget: HTMLElement | null = null;

  /** Native FullCalendar events, bound declaratively to `<full-calendar [events]>`. */
  readonly events = input<EventInput[] | null>([]);

  /** Dims the calendar and shows a centered spinner while data loads. */
  readonly loading = input(false);

  /** Caller options, shallow-merged over the base (caller wins). */
  readonly options = input<CalendarOptions>({});

  /**
   * When true (default), time-grid views scroll to the event nearest to now after
   * the visible range changes / events land. Interactive surfaces that mutate events
   * frequently (assignment drag-drop) should turn this off and call
   * {@link requestScrollToNearestEvent} only when they want a jump.
   */
  readonly autoScrollToNearestEvent = input(true);

  /** Emitted whenever the visible range changes (as `YYYY-MM-DD` days). */
  readonly rangeChanged = output<CalendarRange>();

  /** Emitted once the FullCalendar instance exists, for imperative use (getApi). */
  readonly ready = output<FullCalendarComponent>();

  @ViewChild(FullCalendarComponent) private fullCalendar?: FullCalendarComponent;

  /**
   * Set on `datesSet` / {@link requestScrollToNearestEvent}; cleared once a scroll
   * actually runs (or we know there is nothing timed to scroll to after events settle).
   * Stays true across empty interim `eventsSet` callbacks so a late agenda load still
   * gets the scroll.
   */
  private scrollPending = false;

  /** The imperative FullCalendar API, available after the view initializes. */
  get api() {
    return this.fullCalendar?.getApi() ?? null;
  }

  /** Shared visual/config defaults; `height` is intentionally left to the caller so a
   *  read-only surface can grow (`'auto'`) while an interactive one drives contentHeight. */
  private readonly base: CalendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
    headerToolbar: {
      // Spaces (not commas) keep prev / next / today as separate buttons.
      left: 'prev next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay',
    },
    locales: allLocales,
    locale: this.locale,
    weekends: true,
    editable: false,
    selectable: false,
    navLinks: false,
    stickyHeaderDates: true,
    slotMinTime: '05:00:00',
    slotMaxTime: '23:00:00',
    slotDuration: '00:10:00',
    scrollTime: '07:00:00',
    allDaySlot: true,
    dayMaxEvents: 3,
    eventContent: calendarEventContent,
  };

  /** Base ⊕ caller options, with `datesSet` / `eventsSet` composed so range + scroll always fire. */
  readonly mergedOptions = computed<CalendarOptions>(() => {
    const opts = this.options() ?? {};
    const callerDatesSet = opts.datesSet;
    const callerEventsSet = opts.eventsSet;
    const callerEventMouseEnter = opts.eventMouseEnter;
    const callerEventMouseLeave = opts.eventMouseLeave;
    const callerEventClick = opts.eventClick;
    return {
      ...this.base,
      ...opts,
      eventContent: opts.eventContent ?? calendarEventContent,
      datesSet: (arg: DatesSetArg) => {
        this.hidePreview();
        callerDatesSet?.(arg);
        this.scrollPending = true;
        this.rangeChanged.emit({
          start: calendarBoundaryToApiDay(arg.startStr),
          end: calendarBoundaryToApiDay(arg.endStr),
        });
        this.tryScrollToNearestEvent();
      },
      eventsSet: (events: EventApi[]) => {
        callerEventsSet?.(events);
        this.tryScrollToNearestEvent(events);
      },
      eventMouseEnter: (arg: EventHoveringArg) => {
        callerEventMouseEnter?.(arg);
        this.onEventMouseEnter(arg);
      },
      eventMouseLeave: (arg: EventHoveringArg) => {
        callerEventMouseLeave?.(arg);
        this.onEventMouseLeave();
      },
      eventClick: (arg: EventClickArg) => {
        this.hidePreview();
        callerEventClick?.(arg);
      },
    };
  });

  ngAfterViewInit(): void {
    if (this.fullCalendar) this.ready.emit(this.fullCalendar);
    this.destroyRef.onDestroy(() => this.hidePreview());
  }

  typeLabel(type: EnEventType | null): string {
    if (type === EnEventType.Session) return $localize`Sessione`;
    return '—';
  }

  onPreviewEnter(): void {
    this.clearHoverTimers();
  }

  onPreviewLeave(): void {
    this.scheduleHidePreview();
  }

  hidePreview(): void {
    this.clearHoverTimers();
    this.previewTarget = null;
    this.eventPopover()?.hide();
  }

  onPreviewShown(): void {
    const popover = this.eventPopover();
    const target = this.previewTarget;
    if (popover && target) this.placePreview(popover, target);
  }

  /**
   * Ask the calendar to scroll to the nearest timed event on the next opportunity
   * (immediately if events are already present). Used by surfaces that reopen a
   * calendar without a `datesSet` (e.g. the top-bar day agenda popover). Always
   * runs even when {@link autoScrollToNearestEvent} is false.
   */
  requestScrollToNearestEvent(): void {
    this.scrollPending = true;
    this.tryScrollToNearestEvent(undefined, true);
  }

  private tryScrollToNearestEvent(events?: EventApi[], force = false): void {
    if (!force && !this.autoScrollToNearestEvent()) return;
    if (!this.scrollPending) return;

    const api = this.api;
    if (!api || !api.view.type.startsWith('timeGrid')) {
      return;
    }

    const list = events ?? api.getEvents();
    const scrollTime = scrollTimeForNearestEvent(list);
    if (!scrollTime) {
      // Keep pending while the list is still empty — agenda data often arrives after
      // the first `eventsSet([])`. Give up once we only have all-day events.
      if (list.length > 0) this.scrollPending = false;
      return;
    }

    api.scrollToTime(scrollTime);
    this.scrollPending = false;
  }

  private onEventMouseEnter(arg: EventHoveringArg): void {
    if (this.loading() || this.isEventDragging(arg)) return;
    this.clearHoverTimers();
    this.showTimer = setTimeout(() => this.openPreview(arg.event, arg.el), EVENT_PREVIEW_SHOW_DELAY_MS);
  }

  private onEventMouseLeave(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    this.scheduleHidePreview();
  }

  private openPreview(event: EventApi, el: HTMLElement): void {
    this.previewTarget = el;
    this.preview.set(this.toPreview(event));
    afterNextRender(
      () => {
        const popover = this.eventPopover();
        const target = this.previewTarget;
        if (!popover || !target) return;
        if (popover.overlayVisible) {
          this.placePreview(popover, target);
        } else {
          popover.show(null, target);
        }
      },
      { injector: this.injector },
    );
  }

  private scheduleHidePreview(): void {
    this.clearHideTimer();
    this.hideTimer = setTimeout(() => this.hidePreview(), EVENT_PREVIEW_HIDE_DELAY_MS);
  }

  private clearHoverTimers(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    this.clearHideTimer();
  }

  private clearHideTimer(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  private isEventDragging(arg: EventHoveringArg): boolean {
    return arg.el.classList.contains('fc-event-dragging') || !!arg.el.closest('.fc-event-dragging');
  }

  private placePreview(popover: Popover, target: HTMLElement): void {
    popover.target = target;
    const morePopover = target.closest(FC_MORE_POPOVER_SELECTOR);
    if (morePopover instanceof HTMLElement && popover.container) {
      positionOverlayBeside(popover.container, morePopover);
      return;
    }
    popover.align();
  }

  private toPreview(event: EventApi): CalendarEventPreview {
    const props = event.extendedProps ?? {};
    const status = (props['status'] as EnEventStatus | undefined) ?? null;
    const start = event.start;
    const end = event.end;
    let durationSeconds = typeof props['duration'] === 'number' ? props['duration'] : null;
    if (durationSeconds == null && start && end) {
      durationSeconds = Math.round((end.getTime() - start.getTime()) / 1000);
    }
    const patientName = typeof props['userFullName'] === 'string' ? props['userFullName'] : null;
    return {
      title: event.title ?? '',
      dotColor: event.borderColor || eventStatusColors(status ?? undefined).border,
      patientName: patientName?.trim() || null,
      start,
      end,
      allDay: event.allDay,
      durationSeconds,
      status,
      type: (props['type'] as EnEventType | undefined) ?? EnEventType.Session,
    };
  }
}
